/**
 * Format email signature HTML into properly grouped blocks.
 *
 * Uses the user's own empty paragraphs (<p></p>) as group boundaries.
 * Consecutive non-empty lines are joined with <br> inside a single <p>,
 * giving tight line spacing within each group and paragraph spacing between.
 */

const SIGN_OFFS = [
  'Respectfully,', 'Best regards,', 'Sincerely,', 'Best,',
  'Thank you,', 'Thanks,', 'Regards,', 'Warm regards,', 'Kind regards,',
];

export function formatSignature(html) {
  if (!html) return html;

  // Normalize &nbsp; to regular spaces
  const normalized = html.replace(/&nbsp;/g, ' ');

  // Find earliest sign-off
  let signOffFound = null;
  let signOffPos = normalized.length + 1;
  for (const so of SIGN_OFFS) {
    const idx = normalized.indexOf(so);
    if (idx !== -1 && idx < signOffPos) {
      signOffPos = idx;
      signOffFound = so;
    }
  }
  if (!signOffFound) return normalized;

  // Find the <p tag containing the sign-off
  const pStart = normalized.lastIndexOf('<p', signOffPos);
  if (pStart === -1) return normalized;

  const before = normalized.substring(0, pStart);
  const sigHtml = normalized.substring(pStart);

  // Parse all <p> tags, preserving empty ones as group separators
  const entries = []; // { content: string, isEmpty: boolean }
  const pRegex = /<p[^>]*>(.*?)<\/p>/gs;
  let match;
  while ((match = pRegex.exec(sigHtml)) !== null) {
    const raw = match[1].trim();
    const stripped = raw.replace(/<[^>]+>/g, '').replace(/<br\s*\/?>/g, '').trim();
    entries.push({ content: raw, isEmpty: !stripped || stripped === '' });
  }
  if (entries.length === 0) return normalized;

  // If any non-empty <p> after the sign-off already contains <br>,
  // the signature is pre-formatted — return as-is to avoid double-processing
  const nonEmpty = entries.filter(e => !e.isEmpty);
  if (nonEmpty.length > 1 && nonEmpty.slice(1).some(e => /<br\s*\/?>/.test(e.content))) {
    return normalized;
  }

  // Group consecutive non-empty entries, splitting on empty ones
  const groups = [];
  let current = [];
  for (const entry of entries) {
    if (entry.isEmpty) {
      if (current.length > 0) {
        groups.push(current);
        current = [];
      }
    } else {
      // Flatten any <br> within the entry into separate sub-lines
      const subLines = entry.content.split(/<br\s*\/?>/);
      for (const sl of subLines) {
        const t = sl.trim();
        if (t) current.push(t);
      }
    }
  }
  if (current.length > 0) groups.push(current);

  if (groups.length === 0) return normalized;

  // Build output: each group becomes a <p> with internal <br> separators
  const result = groups.map(g => `<p>${g.join('<br>')}</p>`).join('');
  return before + result;
}
